import { TZDateMini } from '@date-fns/tz';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import _snakeCase from 'lodash/snakeCase';
import snakecaseKeys from 'snakecase-keys';
import type { SnakeCase, SnakeCasedProperties } from 'type-fest';

import type {
  Field,
  PaginationSort,
  SerializedPaginationParams,
  TableViewProps,
} from '@models';
import type { ReportFilterList } from '@features/reports/reports.models';

import { getQueryFilters } from './graphql/graphql-spec-transformer';

/**
 * Given a normalized sort object, convert it to an array of objects with a set
 * id and desc value for use by ReactTable
 * @param sort - Key-value pairs of column id and sort direction
 * @returns Array of objects ingested by ReactTable to determine sort order
 */
export const convertToTableSort = (
  sort: Partial<Record<string, boolean>> = {},
) => Object.keys(sort).map(key => ({ id: key, desc: Boolean(sort[key]) }));

/**
 * Given the sort array from ReactTable, normalize it to key-value pairs of column
 * id and sort direction (true for descending, false for ascending) to easily
 * pass it through to the service
 * @param tableSort - Array returned by ReactTable on sort change
 * @returns Object with keys as column ids and values as sort direction
 */
export const normalizeTableSort = (
  tableSort: { id: string; desc?: boolean }[],
) =>
  tableSort.reduce(
    (sort, { id, desc }) => {
      if (desc !== undefined) sort[id] = desc;
      return sort;
    },
    {} as Record<string, boolean>,
  );

export const normalizeTableViewState = ({
  page = 0,
  pageSize = 10,
  sort,
}: TableViewProps) => ({
  sort,
  page: page + 1,
  limit: pageSize,
});

/**
 * Transforms a possibly camelCased query object to snake_case, ready to send to
 * Platform APIs.
 */
export const transformQuery = (
  query: Partial<Record<string, unknown>> = {},
): Partial<Record<string, string>> =>
  Object.fromEntries(
    Object.entries(query).map(([key, value]) => [
      _snakeCase(key),
      value?.toString(),
    ]),
  );

export const condenseSortQuery = (
  sort: [string[], (string | boolean)[]],
): [string, string] =>
  sort.map(arr => arr.join() || undefined) as [string, string];

export type TransformSortOptions<FieldName extends string = string> = {
  /**
   * When true, the returned field names and directional flags will be condensed
   * into comma-separated strings instead of arrays.
   */
  condensed?: boolean;
  /**
   * Optional key-value mappings for column names where the client sends a name
   * that differs from the data model. Note that there is usually no need to use
   * this for conversion to snake_case, as that is already handled elsewhere.
   */
  fieldMappings?: Record<FieldName, string>;
};

export type TransformSortReturn<
  FieldName extends string,
  DescendingValue extends string | boolean,
  Options extends TransformSortOptions<FieldName> | undefined,
> = Options extends { condensed: true }
  ? [string, string]
  : [SnakeCase<FieldName>[], DescendingValue[]];

export function transformToOrderBy<
  FieldName extends string,
  DescendingValue extends string | boolean,
  Options extends TransformSortOptions<FieldName> | undefined,
>(
  sort?: PaginationSort<FieldName, DescendingValue>,
  options?: Options,
): TransformSortReturn<FieldName, DescendingValue, Options>;

/**
 * Converts a sort object into a tuple of orderBy and orderByDescending values.
 * Values are returned as arrays by default (as expected by the API Clients) but
 * can be optionally condensed into comma-separated strings for legacy services.
 */
export function transformToOrderBy<
  FieldName extends string,
  DescendingValue extends string | boolean,
>(
  sort: PaginationSort<FieldName, DescendingValue> = {},
  options?: TransformSortOptions<FieldName>,
) {
  const entries = Object.entries(sort) as [FieldName, DescendingValue][];

  const transformed = entries.reduce<
    [SnakeCase<FieldName>[], DescendingValue[]]
  >(
    ([orderBy, orderByDescending], [key, value]) => {
      const option = (options?.fieldMappings?.[key] ||
        _snakeCase(key)) as SnakeCase<FieldName>;

      if (
        option &&
        value !== undefined &&
        (typeof value === 'boolean' || value.trim().length)
      ) {
        orderBy.push(option);
        orderByDescending.push(value);
      }

      return [orderBy, orderByDescending];
    },
    [[], []],
  );

  return options?.condensed ? condenseSortQuery(transformed) : transformed;
}

/**
 * Given the sort query object, convert it to a tuple of order by and order by
 * descending strings to pass to the API
 *
 * @deprecated Use {@link transformToOrderBy}
 *
 * @param sort - Sort object passed from the client via query params
 * @param fieldMappings - Key-value mappings for column ids used in the client (key) vs service (value). If not provided, each key is converted to snake_case
 * @returns Tuple of order by and order by descending strings
 */
export const convertSortQuery = <
  FieldName extends string,
  DescendingValue extends string | boolean,
>(
  sort: PaginationSort<FieldName, DescendingValue> = {},
  fieldMappings?: Record<string, string>,
) => transformToOrderBy(sort, { fieldMappings, condensed: true });

export type PlatformQueryReturn<
  Params,
  FieldName extends string,
  DescendingValue extends string | boolean,
  Options extends TransformSortOptions<FieldName> | undefined,
> = SnakeCasedProperties<
  Params & {
    orderBy?: TransformSortReturn<FieldName, DescendingValue, Options>[0];
    orderByDescending?: TransformSortReturn<
      FieldName,
      DescendingValue,
      Options
    >[1];
  }
>;

export function preparePlatformQuery<
  Params extends Record<string, unknown>,
  SortableFieldName extends string,
  SortDescendingValue extends string | boolean,
  SortOptions extends TransformSortOptions<SortableFieldName> | undefined,
>(
  params: Params & {
    sort?: PaginationSort<SortableFieldName, SortDescendingValue>;
  },
  sortOptions?: SortOptions,
): PlatformQueryReturn<
  Params,
  SortableFieldName,
  SortDescendingValue,
  SortOptions
>;

/**
 * Prepares abstract query parameters to interface with Platform APIs. Keys are
 * converted to snake_case. If a sort object is provided, it is transformed and
 * mapped to the recognized parameters.
 */
export function preparePlatformQuery<
  Params extends Record<string, unknown>,
  SortableFieldName extends string,
  SortDescendingValue extends string | boolean,
  SortOptions extends TransformSortOptions<SortableFieldName> | undefined,
>(
  {
    sort,
    ...params
  }: Params & { sort?: PaginationSort<SortableFieldName, SortDescendingValue> },
  sortOptions?: SortOptions,
) {
  const [orderBy, orderByDescending] = transformToOrderBy(
    sort,
    sortOptions,
  ).map(value => value || undefined);

  return snakecaseKeys({
    ...params,
    orderBy,
    orderByDescending,
  });
}

/**
 * Returns query parameters to apply sorting to Platform APIs
 */
export const prepareSortQuery = <
  FieldName extends string,
  DescendingValue extends string | boolean,
>(
  sort: PaginationSort<FieldName, DescendingValue> = {},
  fieldMappings?: Record<string, string>,
) => {
  const [orderBy, orderByDescending] = transformToOrderBy(sort, fieldMappings);

  return snakecaseKeys({ orderBy, orderByDescending });
};

/**
 * Transforms server-side pagination query params to interface with paginated
 * Platform APIs.
 *
 * @deprecated Use {@link preparePlatformQuery}
 */
export const getPaginationQuery = <
  FieldName extends string,
  Params extends SerializedPaginationParams<FieldName>,
>(
  params: Params,
  fieldMappings?: Record<string, string>,
) => preparePlatformQuery(params, { fieldMappings, condensed: true });

/**
 * This utility function ensures correct sorting on alphanumeric strings such as
 * vulnerability identifiers, feed group names, or tag names.
 *
 * Example cases:
 *
 * CVE-2019-9999 should come before CVE-2019-10001
 * alpine:3.10 should come after alpine:3.2
 */
export const sortAlphanumeric = (a: unknown, b: unknown) => {
  // Check for `null` or `undefined` and push them to the bottom
  const isANullOrUndefined = a === null || a === undefined;
  const isBNullOrUndefined = b === null || b === undefined;
  if (isANullOrUndefined && isBNullOrUndefined) return 0;
  if (isANullOrUndefined) return 1;
  if (isBNullOrUndefined) return -1;

  // For non-string values, attempt direct comparison first
  if (typeof a !== 'string' && typeof b !== 'string') {
    return a > b ? 1 : a < b ? -1 : 0;
  }

  // Convert to strings for a case-insensitive, alphanumeric comparison
  return a
    .toString()
    .toLowerCase()
    .localeCompare(b.toString().toLowerCase(), undefined, { numeric: true });
};

/**
 * This utility function sorts dates in descending chronological order (newest to oldest).
 * Null, undefined, and -1 values (used to indicate "no date available") are pushed to the bottom.
 *
 * Example cases:
 *
 * '2023-01-02T00:00:00Z' comes before '2023-01-01T00:00:00Z' (newer dates first)
 * null, undefined, and -1 values are pushed to the bottom
 */
export const sortDateDescending = (
  aVal?: string | number | null,
  bVal?: string | number | null,
) => {
  // Handle null/undefined values by pushing them to the bottom
  if (!aVal && !bVal) return 0;
  if (!aVal || aVal === -1) return 1;
  if (!bVal || bVal === -1) return -1;

  // Convert to Date objects and compare using getTime() for numeric comparison
  // Returns positive if a < b (b comes first), negative if a > b (a comes first)
  return new Date(bVal).getTime() - new Date(aVal).getTime();
};

/**
 * This is a utility function that takes a number of bytes and returns a
 * human-readable string representation of the value.
 * @param {number|string} val Number of bytes
 * @returns {string} Human-readable string representation of the value
 */
export const normalizeBytes = (val: number | string) => {
  let bytes;
  if (typeof val === 'string') {
    bytes = parseInt(val, 10);
    if (Number.isNaN(bytes)) {
      return 'Unknown';
    }
  } else {
    bytes = val;
  }

  const units = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const unitIndex = Math.max(
    0,
    Math.min(units.length - 1, Math.floor(Math.log2(bytes) / 10)),
  );
  const normalizedBytes = Math.round(bytes / 1024 ** unitIndex);
  return `${normalizedBytes} ${units[unitIndex]}`;
};

export const isFieldEmpty = (field: Field) => {
  let empty: boolean;
  if (field.typeName === 'Boolean') {
    empty = false;
  } else if (field.typeName === 'EnumList' || field.typeName === 'StringList') {
    empty = !field.data?.length;
  } else if (
    field.typeName === 'Int' ||
    field.typeName === 'BigInt' ||
    field.typeName === 'Float'
  ) {
    empty = field.data === undefined;
  } else if (field.typeName === 'DateRange') {
    empty = field.data === undefined;
  } else if (field.typeName === 'KeyValue') {
    empty = field.data === undefined || Object.keys(field.data).length === 0;
  } else if (field.typeName === 'JSON') {
    empty = field.data === undefined;
  } else {
    // String, DateTime, TimePeriod, Enum fields
    empty = !(field.data as string | undefined)?.trim()?.length;
  }
  return empty;
};

// Parse the provided GraphQL query and return a list of report filters
// We assume that the query is of the form:
// query queryName([...]){ graphQlQueryName( [...] filter: {[...]}) {[...]}
// where graphQlQueryName must be a valid GQL query name, that will be used
// to get the filter meta-data enabling us to correctly parse the filter values
export const parseFiltersFromGQLQuery = (query: string): ReportFilterList => {
  // Given a string starting with an opening brace,
  // return the index matching closing brace
  const findMatchingBraceIndex = (
    str: string,
    i = 0,
    inQuote = false,
    braceCount = 0,
  ) => {
    if (i >= str.length)
      throw new Error(`Can't find matching brace in string: ${str}`);
    const s = str[i];
    if (s === '"')
      return findMatchingBraceIndex(str, i + 1, !inQuote, braceCount);
    if (!inQuote) {
      if (s === '{')
        return findMatchingBraceIndex(str, i + 1, inQuote, braceCount + 1);
      if (s === '}') {
        if (braceCount === 1) return i;
        return findMatchingBraceIndex(str, i + 1, inQuote, braceCount - 1);
      }
    }
    return findMatchingBraceIndex(str, i + 1, inQuote, braceCount);
  };

  // Parse a filter value provided as a string, relying on the filter
  // meta-data to get the right data type
  const parseFilterValue = (
    queryName: string,
    key: string,
    valueStr: string,
  ): any => {
    const filters = getQueryFilters(queryName);
    if (!filters) {
      throw new Error(`GraphQL query not found ${queryName}`);
    }
    if (!(key in filters)) {
      throw new Error(
        `Filter key: ${key} not found in graphQL query: ${queryName}`,
      );
    }
    const filterDef = filters[key];
    switch (filterDef.type) {
      case 'Boolean':
        return valueStr === 'true';
      case 'DateTime':
        return new Date(valueStr);
      case 'Int':
        return parseInt(valueStr, 10);
      case 'EnumList':
        if (valueStr[0] !== '[' || valueStr.at(-1) !== ']') {
          throw new Error(
            `Invalid format for EnumList field ${key} for query ${queryName}: value should begin and end with square brackets: ${valueStr}`,
          );
        }
        // If the provided EnumList value is empty, return an empty array
        if (valueStr === '[]') return [];
        return valueStr.substring(1, valueStr.length - 1).split(/\s*,\s*/);
      default:
        return valueStr;
    }
  };

  // Find the next valid primitive value (integer, string literal, array literal)
  // contained in the provided string
  // Returns an array containing the parsed value and the remaining string

  const parseNextPrimitiveValue = (
    str: string,
    i = 0,
    inQuote = false,
    arrayBracketCount = 0,
    valStr = '',
  ): [string, string] => {
    if (i >= str.length) return [valStr, str.substring(i)];

    const s = str[i];
    if (s === '"') {
      return parseNextPrimitiveValue(
        str,
        i + 1,
        !inQuote,
        arrayBracketCount,
        valStr,
      );
    }
    if (!inQuote) {
      if (s === '[') {
        return parseNextPrimitiveValue(
          str,
          i + 1,
          inQuote,
          arrayBracketCount + 1,
          valStr + s,
        );
      }
      if (s === ']') {
        return parseNextPrimitiveValue(
          str,
          i + 1,
          inQuote,
          arrayBracketCount - 1,
          valStr + s,
        );
      }
      if (s === ',' && arrayBracketCount === 0) {
        return [valStr, str.substring(i)];
      }
    }
    return parseNextPrimitiveValue(
      str,
      i + 1,
      inQuote,
      arrayBracketCount,
      valStr + s,
    );
  };

  // Parse the provided string and recursively detect nested field values
  // Returns an object of key/value pairs using the flattened dot-separated key names
  // For example: parseValue('{vuln: {id: 1, test: 2}})') returns {'vuln.id': 1, 'vuln.test': 2}
  const parseValue = (strToParse: string): Record<string, any> => {
    const parseNestedValue = (
      str: string,
      key = '',
    ): [Record<string, any>, string] => {
      if (str[0] === '{') {
        const endIndex = findMatchingBraceIndex(str);
        const findNextNestedValue = (
          remaining: string,
          result: Record<string, any> = {},
        ): [Record<string, any>, string] => {
          const m = remaining.match(/^(?:\s|,)*([a-zA-Z0-9]+):\s*(.*)/);
          if (m) {
            const [, nextKey, rest] = m;
            const res = parseNestedValue(rest, nextKey);
            Object.keys(res[0]).forEach(k => {
              result[key ? `${key}.${k}` : k] = res[0][k];
            });
            return findNextNestedValue(res[1], result);
          }
          return [result, str.substring(endIndex + 1)];
        };
        return findNextNestedValue(str.substring(1, endIndex));
      }
      const [value, remaining] = parseNextPrimitiveValue(str);
      return [{ [key]: value }, remaining];
    };
    const [filterObj] = parseNestedValue(strToParse);
    return filterObj;
  };

  // Parse out the query name and the arguments provided to the query
  const matches = query.match(/\([^)]+\)\s*{\s*([a-zA-Z0-9]+)\(([^)]+)\)/);
  if (!matches) throw new Error(`Malformed GraphQL query: ${query}`);
  const [, queryName, args] = matches;
  if (!queryName || !args) throw new Error(`Malformed GraphQL query: ${query}`);

  // Find the filters argument and get its value
  const filterArgValue = args.match(/filter:\s*({.*})/)?.[1];
  if (!filterArgValue)
    throw new Error(`Filter argument not found in query: ${query}`);

  // Now we can parse the filter keys and values and build a ReportFilterList object
  const filterObj = parseValue(filterArgValue);
  return {
    data: Object.keys(filterObj).reduce(
      (data, key) => ({
        ...data,
        [key]: {
          required: false,
          value: parseFilterValue(queryName, key, filterObj[key]),
        },
      }),
      {},
    ),
    displayOrder: Object.keys(filterObj),
  };
};

/**
 * Simulate a delay with a Promise that resolves after a given timeout
 *
 * @param ms Time in milliseconds to wait before resolving the Promise
 */
export const sleep = (ms: number) =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

/**
 * Run a series of async/promisifed functions sequentially
 */
export async function asyncForEach<T>(
  values: T[],
  callback: (item: T, index: number) => Promise<void>,
) {
  const run = async (index: number) => {
    if (index < values.length) {
      await callback(values[index], index);
      await run(index + 1);
    }
  };
  await run(0);
}

/**
 * Shim to handle runtime environments that don't support `structuredClone` for
 * some reason
 */
export const safeStructuredClone = <T>(val: T): T =>
  process.env.BROWSER && typeof window?.structuredClone === 'function'
    ? window.structuredClone(val)
    : JSON.parse(JSON.stringify(val));

/**
 * Return a formated timestamp relative to today, either in local timezone or UTC
 */
export const formatRelativeTimestamp = (
  date: string,
  utc: boolean = false,
): string => {
  const utcDate = new TZDateMini(date, 'UTC');
  const localDate = new TZDateMini(utcDate);
  return formatDistanceToNow(utc ? utcDate : localDate, { addSuffix: true });
};
